/**
 * Weight Tickets Page
 *
 * Quick entry, review, and compliance reporting.
 * PURELY PRESENTATIONAL - all data comes from hooks.
 *
 * DDD Compliance:
 * - Uses application service hooks for data (useTicketList, usePendingReview)
 * - No business logic or calculations in component
 * - Displays view models from application layer
 * - Handles loading, error, and empty states gracefully
 */

import { useState } from 'react';
import { PageHeader } from '@/components/service-provider/PageHeader';
import {
  useTicketList,
  usePendingReview,
} from '@/application/service-provider/hooks/useWeightTicketData';
import {
  useApproveTicket,
  useRejectTicket,
} from '@/application/service-provider/hooks/useWeightTicketMutations';
import type { TicketCardViewModel } from '@/application/service-provider/view-models/WeightTicketViewModel';

export function TicketsPage() {
  // UI state (not business logic)
  const [activeTab, setActiveTab] = useState<'all' | 'pending' | 'entry'>('all');
  const [activeFilters, setActiveFilters] = useState<{
    status?: string;
    job_id?: string;
    date_range?: string;
  }>({});
  const [selectedTicketId, setSelectedTicketId] = useState<string | undefined>();

  // Fetch tickets with current filters
  const { data, isLoading, error } = useTicketList(activeFilters);

  // Fetch pending review tickets for badge count
  const pendingReview = usePendingReview();

  // Mutations
  const approveTicket = useApproveTicket();
  const rejectTicket = useRejectTicket();

  // Handler for filter changes
  const handleFilterChange = (
    filterType: 'status' | 'job_id' | 'date_range',
    value: string
  ) => {
    setActiveFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
  };

  // Handler for approving ticket
  const handleApprove = (ticketId: string) => {
    if (confirm('Approve this weight ticket?')) {
      approveTicket.mutate(ticketId);
    }
  };

  // Handler for rejecting ticket
  const handleReject = (ticketId: string) => {
    const reason = prompt('Enter rejection reason:');
    if (reason) {
      rejectTicket.mutate({ ticketId, reason });
    }
  };

  // Loading state
  if (isLoading && activeTab === 'all') {
    return (
      <div>
        <PageHeader
          title="Weight Tickets"
          description="Enter, review, and manage weight tickets"
          breadcrumbs={[
            { label: 'Dashboard', href: '/provider/dashboard' },
            { label: 'Weight Tickets' },
          ]}
        />
        <div className="space-y-6">
          <FiltersSkeleton />
          <TicketListSkeleton />
        </div>
      </div>
    );
  }

  // Error state
  if (error && !data && activeTab === 'all') {
    return (
      <div>
        <PageHeader
          title="Weight Tickets"
          description="Enter, review, and manage weight tickets"
          breadcrumbs={[
            { label: 'Dashboard', href: '/provider/dashboard' },
            { label: 'Weight Tickets' },
          ]}
        />
        <ErrorState message={error?.message || 'Failed to load tickets'} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Weight Tickets"
        description="Enter, review, and manage weight tickets"
        breadcrumbs={[
          { label: 'Dashboard', href: '/provider/dashboard' },
          { label: 'Weight Tickets' },
        ]}
        actions={[
          {
            label: 'Quick Entry',
            variant: 'primary',
            onClick: () => setActiveTab('entry'),
          },
        ]}
      />

      {/* Tab Navigation */}
      <div className="mb-6">
        <div className="border-b border-neutral-200">
          <nav className="flex gap-8">
            <TabButton
              label="All Tickets"
              count={data?.summary.totalCount}
              isActive={activeTab === 'all'}
              onClick={() => setActiveTab('all')}
            />
            <TabButton
              label="Pending Review"
              count={pendingReview.data?.count || 0}
              isActive={activeTab === 'pending'}
              onClick={() => {
                setActiveTab('pending');
                setActiveFilters({ status: 'pending' });
              }}
              badge={
                pendingReview.data?.oldestTicket &&
                pendingReview.data.oldestTicket.daysOld > 2
                  ? 'Needs Attention'
                  : undefined
              }
            />
            <TabButton
              label="Quick Entry"
              isActive={activeTab === 'entry'}
              onClick={() => setActiveTab('entry')}
            />
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'all' && data && (
        <div className="space-y-6">
          {/* Filters */}
          <div className="card">
            <div className="card-content">
              {/* Status Filter Pills */}
              <div className="mb-4">
                <label className="text-sm font-medium text-neutral-700 mb-2 block">
                  Status
                </label>
                <div className="flex flex-wrap gap-2">
                  {data.filters.statuses.map((status) => (
                    <FilterPill
                      key={status.value}
                      label={status.label}
                      count={status.count}
                      isActive={status.isActive}
                      onClick={() => handleFilterChange('status', status.value)}
                    />
                  ))}
                </div>
              </div>

              {/* Job and Date Range Filters */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Job Dropdown */}
                {data.filters.jobs.length > 0 && (
                  <div>
                    <label
                      htmlFor="job"
                      className="text-sm font-medium text-neutral-700 mb-2 block"
                    >
                      Filter by Job
                    </label>
                    <select
                      id="job"
                      value={activeFilters.job_id || 'all'}
                      onChange={(e) => handleFilterChange('job_id', e.target.value)}
                      className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="all">All Jobs</option>
                      {data.filters.jobs.map((job) => (
                        <option key={job.value} value={job.value}>
                          {job.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Date Range Dropdown */}
                <div>
                  <label
                    htmlFor="dateRange"
                    className="text-sm font-medium text-neutral-700 mb-2 block"
                  >
                    Date Range
                  </label>
                  <select
                    id="dateRange"
                    value={activeFilters.date_range || 'all'}
                    onChange={(e) =>
                      handleFilterChange('date_range', e.target.value)
                    }
                    className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {data.filters.dateRanges.map((range) => (
                      <option key={range.value} value={range.value}>
                        {range.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-neutral-600">{data.summary.displayText}</p>
          </div>

          {/* Ticket List or Empty State */}
          {data.isEmpty ? (
            <EmptyState message={data.emptyStateMessage} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.tickets.map((ticket) => (
                <TicketCard
                  key={ticket.id}
                  ticket={ticket}
                  onViewDetails={() => setSelectedTicketId(ticket.id)}
                  onApprove={() => handleApprove(ticket.id)}
                  onReject={() => handleReject(ticket.id)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Pending Review Tab */}
      {activeTab === 'pending' && (
        <div className="space-y-6">
          {pendingReview.isLoading && <LoadingState />}
          {pendingReview.error && (
            <ErrorState message={pendingReview.error.message} />
          )}
          {pendingReview.data && (
            <>
              {/* Oldest Ticket Alert */}
              {pendingReview.data.oldestTicket &&
                pendingReview.data.oldestTicket.daysOld > 2 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm font-medium text-yellow-900">
                      Oldest ticket has been pending for{' '}
                      {pendingReview.data.oldestTicket.daysOld} days
                    </p>
                  </div>
                )}

              {pendingReview.data.tickets.length === 0 ? (
                <EmptyState message="No tickets pending review" />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {pendingReview.data.tickets.map((ticket) => (
                    <TicketCard
                      key={ticket.id}
                      ticket={ticket}
                      onViewDetails={() => setSelectedTicketId(ticket.id)}
                      onApprove={() => handleApprove(ticket.id)}
                      onReject={() => handleReject(ticket.id)}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Quick Entry Tab */}
      {activeTab === 'entry' && <QuickEntryPlaceholder />}

      {/* Ticket Detail Modal - Placeholder for now */}
      {selectedTicketId && (
        <TicketDetailModalPlaceholder
          ticketId={selectedTicketId}
          onClose={() => setSelectedTicketId(undefined)}
        />
      )}
    </div>
  );
}

// Helper Components (purely presentational)

function TabButton({
  label,
  count,
  isActive,
  onClick,
  badge,
}: {
  label: string;
  count?: number;
  isActive: boolean;
  onClick: () => void;
  badge?: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors relative ${
        isActive
          ? 'border-primary-500 text-primary-600'
          : 'border-transparent text-neutral-600 hover:text-neutral-900'
      }`}
    >
      {label}
      {count !== undefined && (
        <span className="ml-2 px-2 py-0.5 bg-neutral-100 text-neutral-700 text-xs rounded-full">
          {count}
        </span>
      )}
      {badge && (
        <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded-full">
          {badge}
        </span>
      )}
    </button>
  );
}

function FilterPill({
  label,
  count,
  isActive,
  onClick,
}: {
  label: string;
  count: number;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
        isActive
          ? 'bg-primary-500 text-white'
          : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
      }`}
    >
      {label} ({count})
    </button>
  );
}

function TicketCard({
  ticket,
  onViewDetails,
  onApprove,
  onReject,
}: {
  ticket: TicketCardViewModel;
  onViewDetails: () => void;
  onApprove: () => void;
  onReject: () => void;
}) {
  return (
    <div className="card hover:shadow-lg transition-shadow">
      <div className="card-content">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-neutral-900 mb-1">
              {ticket.ticketNumber}
            </h3>
            <p className="text-sm text-neutral-600">{ticket.jobDisplayName}</p>
          </div>
          <StatusBadge badge={ticket.statusBadge} />
        </div>

        {/* Facility and Material */}
        <div className="mb-3 space-y-1">
          <p className="text-sm text-neutral-700">
            <span className="font-medium">Facility:</span> {ticket.facilityName}
          </p>
          <p className="text-sm text-neutral-700">
            <span className="font-medium">Material:</span> {ticket.materialType}
          </p>
        </div>

        {/* Weights */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="bg-neutral-50 rounded p-2">
            <p className="text-xs text-neutral-600">Gross</p>
            <p className="text-sm font-medium text-neutral-900">
              {ticket.grossWeight.replace(' lbs', '')}
            </p>
          </div>
          <div className="bg-neutral-50 rounded p-2">
            <p className="text-xs text-neutral-600">Tare</p>
            <p className="text-sm font-medium text-neutral-900">
              {ticket.tareWeight.replace(' lbs', '')}
            </p>
          </div>
          <div className="bg-primary-50 rounded p-2">
            <p className="text-xs text-primary-600">Net</p>
            <p className="text-sm font-medium text-primary-900">
              {ticket.netWeight.replace(' lbs', '')}
            </p>
          </div>
        </div>

        {/* Photo Indicator */}
        {ticket.hasPhoto && (
          <div className="mb-3">
            <p className="text-xs text-neutral-600">
              📷 {ticket.photoCount} {ticket.photoCount === 1 ? 'photo' : 'photos'}
            </p>
          </div>
        )}

        {/* Timestamp */}
        <div className="mb-3">
          <p className="text-xs text-neutral-500">{ticket.weighedAtRelative}</p>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={onViewDetails}
            className="flex-1 btn-outline text-sm"
          >
            View Details
          </button>
          {ticket.canApprove && (
            <button
              onClick={onApprove}
              className="btn-primary text-sm px-3"
              title="Approve"
            >
              ✓
            </button>
          )}
          {ticket.canReject && (
            <button
              onClick={onReject}
              className="btn-secondary text-sm px-3"
              title="Reject"
            >
              ✗
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({
  badge,
}: {
  badge: { label: string; color: string; icon: string };
}) {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-800',
    green: 'bg-green-100 text-green-800',
    yellow: 'bg-yellow-100 text-yellow-800',
    red: 'bg-red-100 text-red-800',
    gray: 'bg-gray-100 text-gray-800',
  }[badge.color] || 'bg-gray-100 text-gray-800';

  return (
    <span className={`px-3 py-1 rounded-full text-xs font-medium ${colorClasses}`}>
      {badge.label}
    </span>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="card">
      <div className="card-content text-center py-12">
        <svg
          className="w-16 h-16 text-neutral-400 mx-auto mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        <h3 className="text-lg font-semibold text-neutral-900 mb-2">
          No Tickets Found
        </h3>
        <p className="text-neutral-600">{message}</p>
      </div>
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="card">
      <div className="card-content">
        <div className="text-center py-8">
          <div className="text-red-600 text-4xl mb-4">⚠️</div>
          <h3 className="text-lg font-medium text-neutral-900 mb-2">
            Unable to Load Tickets
          </h3>
          <p className="text-sm text-neutral-600 mb-4">{message}</p>
          <button
            className="btn-primary"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="card">
      <div className="card-content py-12 text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-neutral-200 border-t-primary-500 mb-4"></div>
        <p className="text-neutral-600">Loading tickets...</p>
      </div>
    </div>
  );
}

function FiltersSkeleton() {
  return (
    <div className="card">
      <div className="card-content animate-pulse">
        <div className="mb-4">
          <div className="h-4 bg-neutral-200 rounded w-16 mb-2"></div>
          <div className="flex gap-2">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-10 w-24 bg-neutral-200 rounded-full"></div>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="h-4 bg-neutral-200 rounded w-20 mb-2"></div>
            <div className="h-10 bg-neutral-200 rounded"></div>
          </div>
          <div>
            <div className="h-4 bg-neutral-200 rounded w-20 mb-2"></div>
            <div className="h-10 bg-neutral-200 rounded"></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TicketListSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 animate-pulse">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} className="card">
          <div className="card-content">
            <div className="flex justify-between mb-3">
              <div className="flex-1">
                <div className="h-6 bg-neutral-200 rounded w-32 mb-2"></div>
                <div className="h-4 bg-neutral-200 rounded w-full"></div>
              </div>
              <div className="h-6 w-20 bg-neutral-200 rounded-full"></div>
            </div>
            <div className="space-y-2 mb-3">
              <div className="h-4 bg-neutral-200 rounded w-full"></div>
              <div className="h-4 bg-neutral-200 rounded w-3/4"></div>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div className="h-12 bg-neutral-100 rounded"></div>
              <div className="h-12 bg-neutral-100 rounded"></div>
              <div className="h-12 bg-neutral-100 rounded"></div>
            </div>
            <div className="h-10 bg-neutral-200 rounded"></div>
          </div>
        </div>
      ))}
    </div>
  );
}

function QuickEntryPlaceholder() {
  return (
    <div className="card">
      <div className="card-content text-center py-12">
        <svg
          className="w-16 h-16 text-neutral-400 mx-auto mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 4v16m8-8H4"
          />
        </svg>
        <h3 className="text-lg font-semibold text-neutral-900 mb-2">
          Quick Entry Form - Coming Soon
        </h3>
        <p className="text-neutral-600 mb-4">
          Quick weight ticket entry form with photo upload and OCR.
        </p>
        <p className="text-sm text-neutral-500">
          Phase 2 - Core Operations Implementation
        </p>
      </div>
    </div>
  );
}

function TicketDetailModalPlaceholder({
  ticketId,
  onClose,
}: {
  ticketId: string;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Ticket Detail - Coming Soon</h3>
          <button
            onClick={onClose}
            className="text-neutral-500 hover:text-neutral-700"
          >
            ✕
          </button>
        </div>
        <p className="text-sm text-neutral-600 mb-4">
          Ticket ID: {ticketId}
        </p>
        <p className="text-sm text-neutral-600 mb-4">
          Full ticket detail view with photo gallery, approval workflow, and edit
          capabilities coming in Phase 2.
        </p>
        <button className="btn-primary w-full" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}
